// El botón `?` de cada cuadro y su modal explicativo (`showTt` del mockup).
//
// Los textos son los del mock, con UNA regla encima: nada que describa un cuadro que la pantalla no
// pinta. Las viñetas de «Aprobación del motor» y de la mezcla por segmento se quitaron porque
// explicarían algo que no está — y los dos cuadros que no existen (conversión y rechazos) no tienen
// botón. Lo que falta se dice al pie de la pantalla, no acá.
import { useState } from 'react';
import { Eye, FileText, Target } from 'lucide-react';
import { Modal } from '@/kit';

/** Las tres secciones del mock, en su orden. */
type HelpSectionKind = 'what' | 'how' | 'why';

interface HelpItem {
    term: string;
    text: string;
}

interface HelpSection {
    kind: HelpSectionKind;
    intro: string;
    items?: HelpItem[];
    note?: string;
}

interface HelpTopic {
    title: string;
    sections: HelpSection[];
}

export type OriginationHelpTopic = 'trend' | 'indicators' | 'howWeOriginate' | 'ranking';

const SECTION_LABEL: Record<HelpSectionKind, string> = {
    what: 'Qué muestra',
    how: 'Cómo se calcula',
    why: 'Para qué sirve',
};

const SECTION_ICON = {
    what: Eye,
    how: FileText,
    why: Target,
};

const HELP: Record<OriginationHelpTopic, HelpTopic> = {
    trend: {
        title: 'Tendencia día por día',
        sections: [
            {
                kind: 'what',
                intro: 'Barras apiladas por día con la mezcla del mercado, en dos vistas intercambiables (toggle arriba a la derecha):',
                items: [
                    { term: 'Créditos', text: 'número de operaciones cerradas por día' },
                    { term: 'Pesos', text: 'monto en MXN de esas operaciones' },
                ],
                note: 'Segmentos apilados: SureKeep (crédito propio) · Otras financieras · Contado.',
            },
            {
                kind: 'how',
                intro: 'Registro de ventas de toda la red, agrupado por día y por método de pago capturado en el punto de venta.',
                items: [
                    { term: 'Vista Créditos', text: 'conteo de operaciones' },
                    { term: 'Vista Pesos', text: 'suma del monto financiado o cobrado' },
                ],
                note: 'La ventana (7 / 30 / 90 días) se elige en la barra de arriba y aplica a todo el tablero.',
            },
            {
                kind: 'why',
                intro: 'Detectar cambios en la captura del crédito SureKeep frente al mercado.',
                items: [
                    { term: 'Créditos', text: '¿cerramos más o menos operaciones?' },
                    { term: 'Pesos', text: '¿originamos más o menos monto?' },
                ],
                note: 'Ejemplo: SK ↓ en Créditos + ↑ en Pesos = menos operaciones pero más grandes — revisar la mezcla de producto.',
            },
        ],
    },
    indicators: {
        title: 'Indicadores de la ventana',
        sections: [
            {
                kind: 'what',
                intro: 'Los indicadores que resumen la salud de la originación en la ventana seleccionada. Cada uno con su variación contra la ventana anterior comparable (últimos 7 días contra los 7 días previos, etc.).',
                items: [
                    { term: 'Volumen originado SK', text: 'cuántos créditos y cuánto monto' },
                    {
                        term: 'Captura del mercado',
                        text: '% SureKeep contra el total (SK + otras financieras + contado)',
                    },
                    {
                        term: 'Cosecha 1ª cuota a 14d',
                        text: '% de créditos elegibles que pagaron la primera cuota a tiempo',
                    },
                ],
                note: '«Aprobación del motor», el cuarto indicador del mockup, todavía no se puede leer por ventana: no se pinta.',
            },
            {
                kind: 'how',
                intro: 'Se toma la ventana activa y la ventana anterior de la misma duración.',
                items: [
                    {
                        term: 'Volumen',
                        text: 'conteo de créditos SK más la suma del monto financiado',
                    },
                    {
                        term: 'Captura',
                        text: 'créditos SK / (créditos SK + otras financieras + contado)',
                    },
                    {
                        term: 'Cosecha',
                        text: 'de los créditos con 14 días o más de antigüedad, cuántos pagaron su primera cuota',
                    },
                ],
                note: 'La variación va en pp (puntos porcentuales) para las tasas y en % para el volumen. Sin elegibles la cosecha dice N/D, nunca 0 %.',
            },
            {
                kind: 'why',
                intro: 'Termómetro rápido de la originación. Juntos cuentan una historia:',
                items: [
                    {
                        term: 'Volumen ↑ y Captura ↓',
                        text: 'crece el mercado pero perdemos participación',
                    },
                    {
                        term: 'Cosecha ↓',
                        text: 'los clientes recientes pagan peor — señal temprana de deterioro',
                    },
                ],
            },
        ],
    },
    howWeOriginate: {
        title: 'Cómo originamos',
        sections: [
            {
                kind: 'what',
                intro: 'Dos vistas complementarias del portafolio originado en la ventana:',
                items: [
                    {
                        term: 'Izquierda',
                        text: 'mezcla por nivel de riesgo (Bronce · Plata · Oro) con el enganche promedio de cada uno',
                    },
                    {
                        term: 'Derecha',
                        text: 'scatter por tienda: eje X = SCI promedio, eje Y = % de 1ª cuota pagada a 14d, tamaño del punto = volumen SK, color = captura',
                    },
                ],
                note: '«Empleado» no es un nivel: es una venta con plan propio y se cuenta aparte, fuera de las barras y del SCI promedio.',
            },
            {
                kind: 'how',
                intro: 'Se toman los créditos originados en la ventana y se clasifican por nivel de riesgo y por tienda de origen.',
                items: [
                    { term: 'Mezcla', text: 'créditos de cada nivel sobre el total con nivel' },
                    {
                        term: 'Enganche promedio',
                        text: 'enganche cobrado sobre el precio del equipo, contra el mínimo que exigía el plan',
                    },
                    { term: 'SCI tienda', text: 'promedio del SCI de sus créditos, sin empleados' },
                    {
                        term: 'Cosecha 14d',
                        text: 'de los créditos con 14 días o más, cuántos pagaron la primera cuota',
                    },
                ],
                note: 'Cortes de cuadrante: SCI = 60, Cosecha = 60 %.',
            },
            {
                kind: 'why',
                intro: 'Ver de un vistazo qué tipo de cliente estamos originando y desde qué tiendas.',
                items: [
                    { term: 'MODELO', text: 'SCI alto y cosecha alta — el motor calibra bien' },
                    {
                        term: 'SUERTE',
                        text: 'SCI bajo pero cosecha alta — el motor va conservador, podríamos aflojar',
                    },
                    {
                        term: 'ALGO PASA',
                        text: 'SCI alto pero cosecha baja — buen cliente y algo lo tumba (¿experiencia? ¿mora estructural?)',
                    },
                    {
                        term: 'PROBLEMA',
                        text: 'SCI bajo y cosecha baja — tienda a intervenir',
                    },
                ],
                note: 'Ejemplo: un punto grande en PROBLEMA es una tienda con mucho volumen y mala cosecha, la que más le duele al portafolio. Visita urgente y revisar la calibración local.',
            },
        ],
    },
    ranking: {
        title: 'Ranking por tienda',
        sections: [
            {
                kind: 'what',
                intro: 'Tabla de tiendas ordenada por volumen SK, con columnas comparables para detectar tiendas atípicas. Al hacer clic en una fila se resalta su punto en el scatter de «Cómo originamos».',
                items: [
                    { term: 'Vol SK', text: 'créditos SureKeep originados en la ventana' },
                    { term: 'Captura', text: '% SureKeep contra el total vendido por la tienda' },
                    {
                        term: 'Mezcla financ.',
                        text: 'barra con cómo se repartió el financiamiento entre SureKeep y las demás financieras',
                    },
                    { term: 'Enganche', text: 'enganche promedio de sus originaciones' },
                    { term: '1ª cuota 14d', text: '% que pagó puntual (la cosecha de esa tienda)' },
                    { term: 'Δ capt.', text: 'cambio de captura contra la ventana anterior' },
                ],
                note: '«Aprob.» y el detalle por vendedor que el mockup pone acá todavía no tienen dato: no se pintan.',
            },
            {
                kind: 'how',
                intro: 'Se agrupan las ventas de la ventana por tienda de origen. Cada columna se calcula igual que en los indicadores de arriba, pero segmentada por tienda.',
                items: [
                    {
                        term: 'Ordenamiento',
                        text: 'por Vol SK descendente por defecto; el clic en un encabezado lo cambia',
                    },
                    {
                        term: 'Sin base',
                        text: 'lo que el silo manda sin base para calcular va como guion, nunca como cero',
                    },
                ],
                note: 'Sin créditos con 14 días o más de madurez, la cosecha de esa tienda se muestra como N/D.',
            },
            {
                kind: 'why',
                intro: 'Comparar tiendas lado a lado para tomar decisiones operativas concretas.',
                items: [
                    {
                        term: 'Cosecha ↓',
                        text: 'la tienda está mandando clientes que pagan mal aunque pasen el motor',
                    },
                    {
                        term: 'Δ capt. muy positivo',
                        text: 'tienda modelo, estudiar qué está haciendo distinto',
                    },
                    {
                        term: 'Δ capt. muy negativo',
                        text: 'tienda que se está apagando — visita urgente',
                    },
                ],
            },
        ],
    },
};

/** El `?` del encabezado de un cuadro y el modal que abre. */
export function OriginationHelpButton({ topic }: { topic: OriginationHelpTopic }) {
    const [open, setOpen] = useState(false);
    const help = HELP[topic];

    return (
        <>
            <button
                type="button"
                onClick={() => {
                    setOpen(true);
                }}
                className="ml-1.5 inline-flex size-[15px] flex-none items-center justify-center rounded-full border border-border text-[9.5px] font-bold text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                title={`Explicar «${help.title}»`}
                aria-label={`Explicar el cuadro ${help.title}`}
            >
                ?
            </button>
            <Modal
                isOpen={open}
                onClose={() => {
                    setOpen(false);
                }}
                title={help.title}
                size="lg"
            >
                <div className="flex flex-col gap-3">
                    {help.sections.map((section) => {
                        const Icon = SECTION_ICON[section.kind];
                        return (
                            <section key={section.kind} className="flex gap-2.5">
                                <span className="mt-px flex size-6 flex-none items-center justify-center rounded-full bg-muted text-muted-foreground">
                                    <Icon className="size-3.5" aria-hidden />
                                </span>
                                <div className="min-w-0 text-[12px] leading-relaxed">
                                    <h3 className="mb-1 text-[10px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                                        {SECTION_LABEL[section.kind]}
                                    </h3>
                                    <p>{section.intro}</p>
                                    {section.items ? (
                                        <ul className="mt-1.5 flex list-disc flex-col gap-1 pl-4">
                                            {section.items.map((item) => (
                                                <li key={item.term}>
                                                    <strong className="font-semibold">
                                                        {item.term}
                                                    </strong>{' '}
                                                    — {item.text}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : null}
                                    {section.note ? (
                                        <p className="mt-1.5 italic text-muted-foreground">
                                            {section.note}
                                        </p>
                                    ) : null}
                                </div>
                            </section>
                        );
                    })}
                </div>
            </Modal>
        </>
    );
}
